import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { uploadApi } from '../services/api';
import { 
  User, 
  Camera, 
  Globe, 
  CheckCircle, 
  ArrowRight,
  Loader2,
  Cake
} from 'lucide-react';
import './OnboardingPage.css';

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const fileInputRef = useRef(null);
  
  const [step, setStep] = useState(1);
  const [profilePicture, setProfilePicture] = useState(
    user?.profile_picture ? uploadApi.getProfilePictureUrl(user.profile_picture) : null
  );
  const [tradingViewUsername, setTradingViewUsername] = useState(user?.trading_view_username || '');
  const [birthdayMonth, setBirthdayMonth] = useState('');
  const [birthdayDay, setBirthdayDay] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const getDaysInMonth = (month) => {
    const daysMap = {
      '01': 31, '02': 29, '03': 31, '04': 30, '05': 31, '06': 30,
      '07': 31, '08': 31, '09': 30, '10': 31, '11': 30, '12': 31
    };
    return daysMap[month] || 31;
  };

  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a JPG, PNG, GIF, or WEBP image');
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      const uploadResult = await uploadApi.uploadImage(file);
      // Save only the relative path to database (not full URL)
      await updateUser({ profile_picture: uploadResult.url });
      // For display, use the URL directly if it's already complete (S3)
      const displayUrl = uploadResult.url.startsWith('http') 
        ? uploadResult.url 
        : uploadApi.getFileUrl(uploadResult.url);
      setProfilePicture(displayUrl);
    } catch (err) {
      console.error('Upload failed:', err);
      setError('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveTradingView = async () => {
    if (!tradingViewUsername.trim()) {
      setError('Please enter your TradingView username');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      await updateUser({ tradingViewUsername: tradingViewUsername.trim() });
      setStep(3); // Move to birthday step
    } catch (err) {
      console.error('Save failed:', err);
      setError('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveBirthday = async () => {
    setIsSaving(true);
    setError('');

    try {
      if (birthdayMonth && birthdayDay) {
        const birthday = `${birthdayMonth}-${birthdayDay.padStart(2, '0')}`;
        await updateUser({ birthday });
      }
      setStep(4); // Move to welcome step
    } catch (err) {
      console.error('Save birthday failed:', err);
      setError('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCompleteOnboarding = async () => {
    setIsSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('hyperdriveToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/complete-onboarding`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Update local user state
        await updateUser({ onboarding_completed: true });
        // Clear tutorial flag so it shows for newly onboarded users
        localStorage.removeItem('hyperdrive_tutorial_completed');
        sessionStorage.removeItem('hyperdrive_tutorial_shown');
        navigate('/dashboard');
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to complete onboarding');
      }
    } catch (err) {
      console.error('Complete onboarding failed:', err);
      setError('Failed to complete. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const canProceedToStep2 = profilePicture !== null;
  const canProceedToStep3 = tradingViewUsername.trim().length > 0;

  return (
    <div className="onboarding-page">
      <div className="onboarding-container">
        <div className="onboarding-header">
          <h1 className="onboarding-logo">HyperDrive</h1>
          <p className="onboarding-subtitle">Complete Your Profile</p>
        </div>

        {/* Progress Steps */}
        <div className="onboarding-progress">
          <div className={`progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <div className="step-circle">
              {step > 1 ? <CheckCircle size={20} /> : '1'}
            </div>
            <span className="step-label">Photo</span>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
            <div className="step-circle">
              {step > 2 ? <CheckCircle size={20} /> : '2'}
            </div>
            <span className="step-label">TradingView</span>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 3 ? 'active' : ''} ${step > 3 ? 'completed' : ''}`}>
            <div className="step-circle">
              {step > 3 ? <CheckCircle size={20} /> : '3'}
            </div>
            <span className="step-label">Birthday</span>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 4 ? 'active' : ''}`}>
            <div className="step-circle">4</div>
            <span className="step-label">Welcome</span>
          </div>
        </div>

        {error && (
          <div className="onboarding-error">
            {error}
          </div>
        )}

        {/* Step 1: Profile Picture */}
        {step === 1 && (
          <div className="onboarding-step">
            <div className="step-icon">
              <Camera size={48} />
            </div>
            <h2 className="step-title">Add Your Profile Photo</h2>
            <p className="step-description">
              Upload a profile picture so other traders can recognize you in the community.
            </p>

            <div className="avatar-upload-section">
              <div className="avatar-preview-large">
                {profilePicture ? (
                  <img src={profilePicture} alt="Profile" />
                ) : (
                  <User size={64} />
                )}
              </div>
              
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleProfilePictureUpload}
                accept="image/jpeg,image/png,image/gif,image/webp"
                style={{ display: 'none' }}
              />
              
              <button 
                className="upload-photo-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                {isUploading ? (
                  <>
                    <Loader2 size={18} className="spinner" />
                    Uploading...
                  </>
                ) : profilePicture ? (
                  <>
                    <Camera size={18} />
                    Change Photo
                  </>
                ) : (
                  <>
                    <Camera size={18} />
                    Upload Photo
                  </>
                )}
              </button>
            </div>

            <button
              className="continue-btn"
              onClick={() => setStep(2)}
              disabled={!canProceedToStep2}
            >
              Continue
              <ArrowRight size={18} />
            </button>
          </div>
        )}

        {/* Step 2: TradingView Username */}
        {step === 2 && (
          <div className="onboarding-step">
            <div className="step-icon">
              <Globe size={48} />
            </div>
            <h2 className="step-title">Link Your TradingView</h2>
            <p className="step-description">
              Enter your TradingView username to connect your charting profile and use the HyperDrive indicator.
            </p>

            <div className="tradingview-input-section">
              <div className="input-wrapper">
                <span className="input-prefix">@</span>
                <input
                  type="text"
                  placeholder="Your TradingView username"
                  value={tradingViewUsername}
                  onChange={(e) => setTradingViewUsername(e.target.value)}
                />
              </div>

              <div className="tradingview-help">
                <h4>How to find your username:</h4>
                <ol>
                  <li>Log in to TradingView.com</li>
                  <li>Click your profile icon in the top left</li>
                  <li>Your username is shown below your profile picture</li>
                  <li>Or check your profile URL: tradingview.com/u/<strong>username</strong></li>
                </ol>
              </div>
            </div>

            <div className="step-buttons">
              <button className="back-btn" onClick={() => setStep(1)}>
                Back
              </button>
              <button
                className="continue-btn"
                onClick={handleSaveTradingView}
                disabled={!canProceedToStep3 || isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader2 size={18} className="spinner" />
                    Saving...
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Birthday */}
        {step === 3 && (
          <div className="onboarding-step">
            <div className="step-icon">
              <Cake size={48} />
            </div>
            <h2 className="step-title">When's Your Birthday?</h2>
            <p className="step-description">
              Let us know when to celebrate with you!
            </p>

            <div className="birthday-input-section">
              <div className="birthday-selects">
                <select
                  className="birthday-select-onboarding"
                  value={birthdayMonth}
                  onChange={(e) => {
                    setBirthdayMonth(e.target.value);
                    if (birthdayDay && parseInt(birthdayDay) > getDaysInMonth(e.target.value)) {
                      setBirthdayDay('');
                    }
                  }}
                  data-testid="onboarding-birthday-month"
                >
                  <option value="">Select Month</option>
                  {months.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                <select
                  className="birthday-select-onboarding"
                  value={birthdayDay}
                  onChange={(e) => setBirthdayDay(e.target.value)}
                  disabled={!birthdayMonth}
                  data-testid="onboarding-birthday-day"
                >
                  <option value="">Select Day</option>
                  {birthdayMonth && [...Array(getDaysInMonth(birthdayMonth))].map((_, i) => (
                    <option key={i + 1} value={String(i + 1).padStart(2, '0')}>{i + 1}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="step-buttons">
              <button className="back-btn" onClick={() => setStep(2)}>
                Back
              </button>
              <button
                className="continue-btn"
                onClick={handleSaveBirthday}
                disabled={isSaving || !birthdayMonth || !birthdayDay}
                data-testid="onboarding-birthday-continue"
              >
                {isSaving ? (
                  <>
                    <Loader2 size={18} className="spinner" />
                    Saving...
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Welcome */}
        {step === 4 && (
          <div className="onboarding-step">
            <div className="step-icon success">
              <CheckCircle size={64} />
            </div>
            <h2 className="step-title">You're All Set!</h2>
            <p className="step-description">
              Welcome to the HyperDrive Trading Community, <strong>{user?.name}</strong>!
            </p>

            <div className="profile-summary">
              <div className="summary-avatar">
                {profilePicture ? (
                  <img src={profilePicture} alt="Profile" />
                ) : (
                  <User size={48} />
                )}
              </div>
              <div className="summary-info">
                <h3>{user?.name}</h3>
                <p className="summary-tradingview">
                  <Globe size={14} />
                  @{tradingViewUsername}
                </p>
                {birthdayMonth && birthdayDay && (
                  <p className="summary-birthday">
                    <Cake size={14} />
                    {months.find(m => m.value === birthdayMonth)?.label} {parseInt(birthdayDay)}
                  </p>
                )}
              </div>
            </div>

            <div className="welcome-features">
              <h4>Here's what you can do:</h4>
              <ul>
                <li><CheckCircle size={16} /> Join live trading sessions with Coach CJ</li>
                <li><CheckCircle size={16} /> Chat with fellow traders in the community</li>
                <li><CheckCircle size={16} /> Track your trades and performance</li>
                <li><CheckCircle size={16} /> Access trading courses and resources</li>
              </ul>
            </div>

            <button
              className="finish-btn"
              onClick={handleCompleteOnboarding}
              disabled={isSaving}
              data-testid="onboarding-finish-btn"
            >
              {isSaving ? (
                <>
                  <Loader2 size={18} className="spinner" />
                  Loading...
                </>
              ) : (
                <>
                  Enter Dashboard
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingPage;
